import { ReactNode, useCallback, useState } from 'react';

import { useEffectOnce } from 'hooks/useEffectOnce';
import { CustomSignupProductType } from 'pages/SignUp/types';
import { CUSTOM_SIGNUP_CONTENT_KEY } from 'utils/consts';

import { defaultPersistentStateContextState, PersistentStateContext } from './persistentStateContext';
import { SetPersistentDataFn } from './types';

export interface PersistentStateProviderProps {
  children: ReactNode;
}

type JSONSafe = string | number | boolean | null | { [key: string]: JSONSafe } | Array<JSONSafe>;

/** This PersistentStateProvider is designed to handle the specific pieces of custom data that need
 * to be accessed up and down the component tree, information that we dont want to keep in query params but
 * dont want to lose the values of altogether. Context Providers such as TrackingProvider and RedirectProvider
 * do some the heavy lifting here in gathering state data and then removing it from query params, but this
 * new Context Provider will keep track of some of this removed data because it is required by the custom signup
 * flow (see files in pages>Signup for more about custom signup flow, functions, and components).
 * Currently the usage here is limited, but given where this is in the component tree, it can hold a lot more.
 *
 */
export const PersistentStateProvider = ({ children }: PersistentStateProviderProps) => {
  const [persistentState, setPersistentState] = useState(defaultPersistentStateContextState);

  useEffectOnce(() => {
    let customSignupProduct: CustomSignupProductType | undefined = undefined;

    try {
      const stored = localStorage.getItem(CUSTOM_SIGNUP_CONTENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // only respect persisted data if it is less than an hour old
        if (typeof parsed.timestamp === 'number' && parsed.timestamp > Date.now() - 1000 * 60 * 60) {
          customSignupProduct = {
            key: String(parsed.custom.key),
            title: String(parsed.custom.title),
            bullets: Array.isArray(parsed.custom.bullets) ? parsed.custom.bullets.map(String) : [],
            imgSrc: String(parsed.custom.imgSrc),
            imgAlt: String(parsed.custom.imgAlt),
            redirect: String(parsed.custom.redirect),
            loginRedirect: 'loginRedirect' in parsed.custom ? String(parsed.custom.loginRedirect) : undefined,
            isPortalRedirect: 'isPortalRedirect' in parsed.custom ? Boolean(parsed.custom.isPortalRedirect) : undefined,
            isHeroImg: 'isHeroImg' in parsed.custom ? Boolean(parsed.custom.isHeroImg) : undefined,
            description: 'description' in parsed.custom ? String(parsed.custom.description) : undefined,
            footer: 'footer' in parsed.custom ? String(parsed.custom.footer) : undefined,
            isNumber: 'isNumber' in parsed.custom ? Boolean(parsed.custom.isNumber) : undefined,
            skipOrgCreation: 'skipOrgCreation' in parsed.custom ? Boolean(parsed.custom.skipOrgCreation) : undefined,
          } satisfies CustomSignupProductType;
        }
      }
    } catch {
      // do nothing, we will just use the default state
    }

    setPersistentState((persistentState) => ({
      ...persistentState,
      initialized: true,
      customContent: customSignupProduct,
    }));
  });

  const setPersistentData: SetPersistentDataFn = useCallback((customContent?: CustomSignupProductType) => {
    setPersistentState((persistentState) => ({
      ...persistentState,
      customContent,
    }));
    try {
      if (customContent) {
        localStorage.setItem(
          CUSTOM_SIGNUP_CONTENT_KEY,
          JSON.stringify({
            timestamp: Date.now(),
            custom: {
              ...customContent,
              // We don't render these properties after the initial Signup page, so we don't need to persist them.
              // This avoids having to serialize + parse JSX elements, which would be dangerous and error-prone.
              title: '',
              bullets: [],
              description: '',
              footer: '',
            } satisfies JSONSafe,
          })
        );
      } else localStorage.removeItem(CUSTOM_SIGNUP_CONTENT_KEY);
    } catch {
      // do nothing, we will just use the default state
    }
  }, []);

  return (
    <PersistentStateContext.Provider
      value={[
        persistentState,
        {
          setPersistentData,
        },
      ]}
    >
      {persistentState.initialized ? children : null}
    </PersistentStateContext.Provider>
  );
};
